import { useSelector } from 'react-redux';
import { BinIcon, PenIcon } from '@rsos/assets/icons';
import { Loader } from '@rsos/base-components';
import { Switch } from '@rsos/components/capstone/base';
import {
  SmartPhraseText,
  useSmartPhraseContext,
} from '@rsos/components/capstone/base/SmartPhrase';
import { useCurrentPSAP } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import { useUpdatePreconfiguredMessageMutation } from '@rsos/reduxDataStore/src/features/preconfiguredMessages';
import type { PreconfiguredResponse } from '@rsos/reduxDataStore/src/features/preconfiguredMessages/preconfiguredMessagesAPI';
import { selectUser } from '@rsos/sinatra';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import { notifyError, notifySuccess } from '@rsos/utils/toastUtils';
import { UPDATE_SUCCESS_MESSAGE } from '../modals/utils';
import styles from './PreconfiguredMessagesTable.module.css';

interface Props {
  message: PreconfiguredResponse;
  onEdit: (message: PreconfiguredResponse) => void;
  onDelete: (message: PreconfiguredResponse) => void;
}

const TableRow = ({ message, onEdit, onDelete }: Props) => {
  const psap = useCurrentPSAP();
  const user = useSelector(selectUser);
  const orgName = useCurrentOrgName() || '';

  const context = useSmartPhraseContext({ psap, user, orgName });
  const [updatePreconfiguredMessage, { isLoading: isUpdating }] =
    useUpdatePreconfiguredMessageMutation();

  const isRsos = message.owner === 'rapidsos';

  const handleToggle = () => {
    updatePreconfiguredMessage({
      org: orgName,
      id: message.id,
      updates: { enabled: !message.enabled },
    })
      .unwrap()
      .then(() => notifySuccess(UPDATE_SUCCESS_MESSAGE))
      .catch((error: unknown) => {
        const err = error as { data?: { message?: string } };
        notifyError(err.data?.message || 'Failed to update message');
      });
  };

  return (
    <tr className={styles.row}>
      <td className={styles.td}>{message.title ?? ''}</td>
      <td className={styles.td}>
        <SmartPhraseText
          text={message.body ?? ''}
          context={context}
          variant="admin"
        />
      </td>
      <td className={styles.td}>
        {isUpdating ? (
          <Loader noText fluid />
        ) : (
          <div
            className={styles.actions}
            role="group"
            aria-label="Message actions"
          >
            <button
              className={styles.iconButton}
              data-tooltip={
                isRsos ? 'System messages cannot be edited' : 'Edit'
              }
              aria-label={`Edit ${message.title}`}
              disabled={isRsos}
              onClick={() => onEdit(message)}
            >
              <PenIcon width={14} height={14} />
            </button>
            <button
              className={styles.iconButton}
              data-tooltip={
                isRsos ? 'System messages cannot be deleted' : 'Delete'
              }
              aria-label={`Delete ${message.title}`}
              disabled={isRsos}
              onClick={() => onDelete(message)}
            >
              <BinIcon width={14} height={14} />
            </button>
            <Switch onOffLabel isOn={message.enabled} onClick={handleToggle} />
          </div>
        )}
      </td>
    </tr>
  );
};

export default TableRow;
